import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function WarwickshireCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Warwickshire Pothole Claims | Four AMBER DfT Ratings | Fixtyer"
        description="Warwickshire County Council scores AMBER on all four DfT scorecards, filled 39,556 potholes in five years, and 13% of its 2,195km of unclassified roads are in RED condition. See what that means for your claim."
        canonical="/councils/warwickshire"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Warwickshire', url: '/councils/warwickshire' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Warwickshire is AMBER on all four DfT scorecards — can I still claim?',
            answer: 'Yes. An AMBER overall rating confirms the Department for Transport considers Warwickshire middling on condition, spend and best practice — not failing, but not good enough either. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired, not on the headline colour. Warwickshire\'s own data shows B and C roads in RED condition up 45% since 2020/21 and 13% of unclassified roads in RED condition — both are claim-relevant facts.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'Unclassified roads make up 2,195km — 56% of Warwickshire\'s 3,929km carriageway network. The council historically surveyed them using Coase Visual Inspection over two years, and only publishes the RED percentage (13% in 2024/25, equivalent to roughly 285km). For 2025/26 it is changing methodology, and the new survey will take three years to complete a full network assessment. Safety inspections are separate from condition surveys — ask when your road was last condition-assessed, not just last driven by an inspector.',
          },
          {
            question: 'Does Warwickshire spending above its DfT allocation weaken my claim?',
            answer: 'No. Warwickshire projects £29.936m capital spend against a £25.465m DfT allocation in 2025/26 — roughly 18% above the grant — yet still earns an AMBER Spend scorecard. The DfT judges spend against network need, not just the headline figure. The council\'s own asset management plan acknowledges that available funding is unlikely to match maintenance need, and that reducing preventative treatments will inevitably worsen annual performance.',
          },
          {
            question: 'The council says its roads compare favourably nationally — does that matter?',
            answer: 'Warwickshire\'s combined classified-road RCI for 2024/25 shows 4.34% RED against 11% for England and Wales — genuinely better on that benchmark. But the DfT still rates condition AMBER overall, B/C RED roads have risen from 2.9% to 4.2%, and the council itself warns that national comparisons are not representative of success or failure at the local level. Your claim is about the specific defect on your road, not Warwickshire\'s ranking against Derbyshire.',
          },
          {
            question: 'Pothole repairs spiked to 11,614 in 2023/24 — does the fall to 10,099 mean roads are fixed?',
            answer: 'No. Warwickshire still filled 10,099 potholes in 2024/25 and expects to permanently repair over 12,000 in the current financial year. The five-year total is 39,556 filled potholes from find-and-fix gangs and reactive repairs combined. A network producing defects at that rate is one where potholes routinely form between inspections — exactly where prior reports and photographic evidence decide claims.',
          },
          {
            question: 'Warwickshire is changing its survey methodology — how does that affect my claim?',
            answer: 'From 2026/27 the council will use BSI PAS 2161, which breaks the existing red/amber/green time series. For 2025/26 it is aligning unclassified road surveys with the classified network methodology, but the survey will take three years for a full network pass. If your incident falls during a transition year, ask which survey standard applied to your road and whether condition data existed at all for that location.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Warwickshire County Council"
        councilSlug="warwickshire"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/warwickshire"
        headline="Warwickshire Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Warwickshire</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Warwickshire: Amber Across the Board
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Warwickshire County Council scores <strong className="text-amber-400">AMBER on all four DfT scorecards</strong> — 
            condition, spend and best practice included. Its own transparency report records{' '}
            <strong className="text-rose-400">39,556 pothole repairs in five years</strong>, a spike to 11,614 in 2023/24, 
            and 13% of its 2,195km unclassified network in RED condition while B and C roads in RED have risen 45% since 2020/21.
          </p>

          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">4/4</div>
            <div className="text-white font-medium mt-2">AMBER DfT scorecards</div>
            <div className="text-brand-300 text-sm mt-1">
              No GREEN escape hatch on spend or best practice. The council exceeds its DfT capital allocation 
              (£29.936m projected vs £25.465m in 2025/26) and still earns AMBER on Spend — the DfT's verdict 
              is that investment is not keeping pace with need.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* Network overview */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The Network Warwickshire Maintains
          </h2>
          <p className="text-surface-600 mb-8">
            3,929km of carriageway across a county where unclassified roads dominate — from the council's own transparency report
          </p>

          <div className="grid sm:grid-cols-3 gap-6 mb-8">
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 text-center">
              <div className="text-3xl font-bold text-brand-900">435km</div>
              <div className="text-sm text-surface-600 mt-1">A roads (11.1%)</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 text-center">
              <div className="text-3xl font-bold text-brand-900">1,299km</div>
              <div className="text-sm text-surface-600 mt-1">B and C roads (33.1%)</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 text-center">
              <div className="text-3xl font-bold text-amber-900">2,195km</div>
              <div className="text-sm text-amber-800 mt-1">U roads (55.9%)</div>
            </div>
          </div>

          <p className="text-surface-600 text-sm mb-8">
            The council also maintains 2,915km of footways, 115,000 gullies, 1,160 structures, 53,000 street lights 
            and 142,000 trees. Motorways and trunk roads are National Highways' responsibility.
          </p>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">Why The Split Matters For Claims</h3>
            <p className="text-brand-200 mb-4">
              More than half of Warwickshire's carriageway is unclassified — estate roads, village routes and residential 
              streets. These roads were historically condition-surveyed using Coase Visual Inspection over two years, 
              not the SCANNER laser surveys used on classified roads. If your pothole was on a U-road, the council's 
              own network-level knowledge of its condition is weaker and slower to update.
            </p>
            <p className="text-white font-medium">
              Under Section 41 of the Highways Act 1980, Warwickshire must maintain its roads. Under Section 58, it can 
              escape liability only if it took such care as was reasonable — and that turns on inspection and repair of 
              the specific defect, not aggregate scorecard colours.
            </p>
          </div>
        </div>
      </section>

      {/* Condition data */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of SCANNER survey data on classified roads — A-roads flat, B/C roads slipping
          </p>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (435km): RED share rising</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020/21</td>
                    <td className="px-4 py-2 text-center">3.5%</td>
                    <td className="px-4 py-2 text-center">28.2%</td>
                    <td className="px-4 py-2 text-center">68.3%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021/22</td>
                    <td className="px-4 py-2 text-center">4.0%</td>
                    <td className="px-4 py-2 text-center">23.7%</td>
                    <td className="px-4 py-2 text-center">72.3%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022/23</td>
                    <td className="px-4 py-2 text-center">4.4%</td>
                    <td className="px-4 py-2 text-center">24.8%</td>
                    <td className="px-4 py-2 text-center">70.8%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023/24</td>
                    <td className="px-4 py-2 text-center">4.2%</td>
                    <td className="px-4 py-2 text-center">28.9%</td>
                    <td className="px-4 py-2 text-center">66.9%</td>
                  </tr>
                  <tr className="bg-surface-50">
                    <td className="px-4 py-2 font-mono font-bold">2024/25</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">4.5%</td>
                    <td className="px-4 py-2 text-center font-bold">28.6%</td>
                    <td className="px-4 py-2 text-center font-bold">66.9%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              A-road RED condition is up from 3.5% to 4.5% over five years. Green roads have fallen from 68.3% to 66.9%. 
              SCANNER data is collected annually in one direction, alternated so both lanes are covered over two years.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (1,299km): declining</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020/21</td>
                    <td className="px-4 py-2 text-center">2.9%</td>
                    <td className="px-4 py-2 text-center">23.0%</td>
                    <td className="px-4 py-2 text-center">74.1%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021/22</td>
                    <td className="px-4 py-2 text-center">3.1%</td>
                    <td className="px-4 py-2 text-center">19.5%</td>
                    <td className="px-4 py-2 text-center">77.4%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022/23</td>
                    <td className="px-4 py-2 text-center">3.7%</td>
                    <td className="px-4 py-2 text-center">20.6%</td>
                    <td className="px-4 py-2 text-center">75.7%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023/24</td>
                    <td className="px-4 py-2 text-center">4.4%</td>
                    <td className="px-4 py-2 text-center">24.3%</td>
                    <td className="px-4 py-2 text-center">71.4%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024/25</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">4.2%</td>
                    <td className="px-4 py-2 text-center font-bold">24.3%</td>
                    <td className="px-4 py-2 text-center font-bold">71.5%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              RED-condition B/C roads are up 45% since 2020/21 (2.9% → 4.2%). Combined RED and Amber share is 
              28.5% in 2024/25 — roughly 370km of local classified roads needing maintenance now or soon. 
              Green roads have fallen from 74.1% to 71.5%.
            </p>
          </div>

          {/* National comparison */}
          <div className="bg-white rounded-2xl border border-surface-200 p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">Warwickshire vs England and Wales (2024/25 combined RCI)</h3>
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-sm text-surface-500 mb-2">RED</div>
                <div className="text-2xl font-bold text-rose-600">4.34%</div>
                <div className="text-xs text-surface-500">Warwickshire vs 11% nationally</div>
              </div>
              <div className="text-center">
                <div className="text-sm text-surface-500 mb-2">Amber</div>
                <div className="text-2xl font-bold text-amber-600">25.83%</div>
                <div className="text-xs text-surface-500">Warwickshire vs 32% nationally</div>
              </div>
              <div className="text-center">
                <div className="text-sm text-surface-500 mb-2">Green</div>
                <div className="text-2xl font-bold text-emerald-600">71.80%</div>
                <div className="text-xs text-surface-500">Warwickshire vs 57% nationally</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm mt-4">
              The council's asset management plan states classified-road condition "currently compares favourably" 
              to other authorities. The DfT still rates Warwickshire AMBER on condition overall — national benchmarking 
              and your specific claim are different questions.
            </p>
          </div>
        </div>
      </section>

      {/* U-road blind spot */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 2,195km Unclassified Question
          </h2>
          <p className="text-surface-600 mb-8">
            56% of the network — but only RED percentages published, and the survey method is changing
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">U-roads in RED condition</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">17%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">17%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">11%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">13%</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">13%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Data on unclassified road network has been historically collected using a Coase Visual Inspection (CVI). The data was collected over two years for the whole network. This duplicated some of the data collected on the classified road network by the SCANNER survey. For 2025/26, we are changing condition assessment method to be in line with the survey methodology for the classified network."
              source="Warwickshire County Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="The survey will take three years to complete a full network assessment; however, the higher used roads will be completed in two years in line with the classified network."
              source="Warwickshire County Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">~285km of U-Roads in RED</h3>
              <p className="text-surface-600 text-sm mb-3">
                At 13% RED on 2,195km, roughly 285km of Warwickshire's residential and village roads are in the 
                worst published condition category. The council does not publish Amber or Green percentages for 
                unclassified roads — only RED — so the full picture on the majority of its network is incomplete 
                in the transparency report.
              </p>
              <p className="text-surface-600 text-sm">
                CVI data was collected over two years for the whole U-road network. That is slower and less 
                granular than annual SCANNER surveys on classified roads.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The PAS 2161 Transition</h3>
              <p className="text-amber-800 text-sm mb-3">
                From 2026/27 a new BSI PAS 2161 methodology will replace the three-category red/amber/green system, 
                creating a break in the time series. The DfT plans to dual-run standards in 2025 and 2026 before 
                full adoption in 2027.
              </p>
              <p className="text-amber-800 text-sm">
                If your claim involves a defect from 2025/26 onwards, ask which survey standard was in force and 
                whether your road had been assessed under the old CVI method, the new aligned methodology, or not at all.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              Safety inspections and condition surveys are not the same thing. Warwickshire's asset management plan 
              is explicit: "Safety Inspections are not part of any formal condition assessment." To rely on Section 58, 
              the council must show reasonable care — ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Was your road on a U-road where only RED percentages are published, not full condition data?</li>
              <li>• When was it last condition-surveyed under CVI — and has the methodology since changed?</li>
              <li>• If 13% of U-roads are RED network-wide, what was done about the specific defect on yours?</li>
              <li>• Does the three-year full-network assessment timeline mean your road may not yet have been surveyed?</li>
            </ul>
            <p className="text-white font-medium">
              A council cannot claim detailed network knowledge on roads it surveys on a multi-year cycle 
              with incomplete published data — then change how it measures mid-stream.
            </p>
          </div>
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            39,556 Potholes in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            Find-and-fix gangs plus reactive repairs from safety inspections and public reports
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">7,285</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">4,650</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">5,908</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">11,614</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">10,099</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">39,556</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The 2023/24 Spike</h3>
              <p className="text-surface-600 text-sm">
                Pothole fills more than doubled from 5,908 in 2022/23 to 11,614 in 2023/24 — roughly 32 repairs 
                every day for a year. The council expects to permanently repair over 12,000 potholes in the current 
                financial year. That volume tells you defects are forming continuously, not occasionally.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Asset Management Framing</h3>
              <p className="text-amber-800 text-sm">
                Warwickshire's own asset management plan states that from an asset management perspective, it would 
                be "hard to justify the prioritisation of pothole repair at the expense of other preventative or 
                structural maintenance works" unless potholes indicate a need for larger intervention. That is 
                the council's own hierarchy — pothole repair is reactive, and they know it.
              </p>
            </div>
          </div>

          <QuoteCard
            quote="From an asset management perspective, it would be hard to justify the prioritisation of pothole repair at the expense of other preventative or structural maintenance works unless their presence is proved to be indicative of a need for larger maintenance intervention works."
            source="Warwickshire County Council Highway Asset Management Plan (Interim), October 2025"
          />
        </div>
      </section>

      {/* Spending */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Spending Above Allocation, Still AMBER
          </h2>
          <p className="text-surface-600 mb-8">
            Warwickshire tops up DfT grants with council funds — but the DfT says it is still not enough
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">DfT allocation</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Capital spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Revenue spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Reactive</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2020/21</td>
                  <td className="px-4 py-3 text-center">£21.791m</td>
                  <td className="px-4 py-3 text-center">£25.208m</td>
                  <td className="px-4 py-3 text-center">£16.138m</td>
                  <td className="px-4 py-3 text-center">74%</td>
                  <td className="px-4 py-3 text-center">26%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2024/25</td>
                  <td className="px-4 py-3 text-center">£18.227m</td>
                  <td className="px-4 py-3 text-center">£22.174m</td>
                  <td className="px-4 py-3 text-center">£20.636m</td>
                  <td className="px-4 py-3 text-center">76%</td>
                  <td className="px-4 py-3 text-center">24%</td>
                </tr>
                <tr className="bg-amber-50">
                  <td className="px-4 py-3 font-mono font-bold">2025/26 (proj.)</td>
                  <td className="px-4 py-3 text-center font-bold">£25.465m</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-600">£29.936m</td>
                  <td className="px-4 py-3 text-center font-bold">£21.256m</td>
                  <td className="px-4 py-3 text-center font-bold">77%</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-600">23%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Preventative Treatments</h3>
              <p className="text-surface-600 text-sm mb-4">
                Surface dressing — sealing roads against water ingress — has risen from 67km in 2020/21 to 82km in 2024/25. 
                Resurfacing has fallen from 42km to 27km over the same period. The council plans 123.53km of surface 
                dressing and 9.81km of carriageway resurfacing in 2025/26.
              </p>
              <div className="grid grid-cols-2 gap-4 text-center text-sm">
                <div>
                  <div className="font-bold text-brand-900">82km</div>
                  <div className="text-surface-500">Surface dressing 2024/25</div>
                </div>
                <div>
                  <div className="font-bold text-rose-600">27km</div>
                  <div className="text-surface-500">Resurfacing 2024/25</div>
                </div>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Funding Gap Admission</h3>
              <p className="text-amber-800 text-sm">
                The council's asset management plan states that "available funding to undertake the required amount 
                of maintenance is unlikely to match the need" and that "reducing treatments that contribute towards 
                improving the network will inevitably result in worsening of annual performance." It also notes that 
                when budgets do not keep pace with inflation and climate pressures, "the acceptable standard is reduced 
                in line with the finances available" — while statutory duties under the Highways Act do not change.
              </p>
            </div>
          </div>

          <QuoteCard
            quote="As such, it becomes increasingly difficult to improve the carriageway asset or maintain it in a steady state. That is not to say that the network becomes unsafe. It is that there must be agreement that either desired standards must be funded through increased budgets, or the acceptable standard is reduced in line with the finances available. The Authority's statutory duties do not change however, and a diminution of acceptable standard does should not mean a compromise in safety for the end user."
            source="Warwickshire County Council Highway Asset Management Plan (Interim), October 2025"
          />
        </div>
      </section>

      {/* Inspection methodology */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Inspections, Claims Registers and Find-and-Fix
          </h2>
          <p className="text-surface-600 mb-8">
            How Warwickshire says it finds defects — and what it admits about pothole priority
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Safety Inspections</h3>
              <p className="text-surface-600 text-sm mb-3">
                Safety inspections are undertaken by the term maintenance contractor to meet statutory duties under 
                the Highways Act. Defects including potholes are recorded with priority responses. These inspections 
                are explicitly not part of any formal condition assessment.
              </p>
              <p className="text-surface-600 text-sm">
                The council also holds a register of third-party claims and uses it to flag hotspots where higher 
                incidences of claims are being made — documented knowledge that certain areas produce more defects.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Find and Fix</h3>
              <p className="text-surface-600 text-sm mb-3">
                Proactive find-and-fix gangs address small defects before they escalate. The council states this 
                approach is "extremely effective in tackling smaller road defects before they escalate into larger, 
                more expensive issues with greater disruption to the road network."
              </p>
              <p className="text-surface-600 text-sm">
                For 2025/26 the council expects over 12,000 permanent pothole repairs plus approximately 300 
                localised carriageway patching repairs and 200 footway patches.
              </p>
            </div>
          </div>

          <QuoteCard
            quote="Our commitment to long-term preventative maintenance over short-term reactive repairs helps extend the lifespan of our highway infrastructure and reduce overall maintenance costs and network disruption. Our proactive 'Find and Fix' pothole repair teams address small defects before they escalate into larger potholes and we invest heavily in surface dressing programmes to seal road surfaces, extend their lifespan and prevent costly repairs."
            source="Warwickshire County Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* Balanced claims section */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a Middling AMBER Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Warwickshire is not Derbyshire, but it is not Buckinghamshire either
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Combined classified-road RCI better than England and Wales average (4.34% RED vs 11%)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Capital spend consistently above DfT allocation — topped up with council revenue
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 77% preventative spend projected for 2025/26 with active find-and-fix gangs
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Ranked 2nd in NHT peer benchmarking survey; ISO accreditation pursuit documented
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> U-road RED share fell from 17% to 13% over five years (though methodology is changing)
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a structured Section 58 defence with asset management documentation. Generic claims will struggle on A-roads.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER on all four scorecards — DfT verdict is middling across the board
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> B/C RED roads up 45% since 2020/21 despite preventative spend claims
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 13% of U-roads in RED (~285km) with incomplete RAG data on 56% of the network
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 39,556 pothole repairs in five years — defects form faster than structural treatment
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits funding gap and reduced acceptable standards when budgets fall short
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Survey methodology changing — three-year U-road assessment cycle creates knowledge gaps
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council that benchmarks well nationally and documents its asset management, your claim lives or dies on 
              the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, warwickshire.gov.uk/potholes) — proof of actual notice</li>
              <li>• Photos showing the defect's size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road's class — on a U-road, the CVI survey cycle and incomplete RAG data are your strongest structural arguments</li>
              <li>• Whether the location appears on the council's third-party claims hotspot register</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and 
              cites Warwickshire's own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Warwickshire?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            Four AMBER scorecards still leave room for a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road CVI survey-gap argument</li>
                <li>• No prior-report search</li>
                <li>• No four-scorecard AMBER context</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ B/C road decline documented</li>
                <li>✅ U-road survey gap and 13% RED argued</li>
                <li>✅ 39,556 repairs in five years cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Warwickshire</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=warwickshire"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Warwickshire Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Warwickshire is AMBER on all four DfT scorecards — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. An AMBER overall rating confirms the Department for Transport considers Warwickshire middling 
                on condition, spend and best practice — not failing, but not good enough either. Section 58 turns on 
                whether the specific defect that damaged your vehicle was reasonably inspected and repaired, not on 
                the headline colour. Warwickshire's own data shows B and C roads in RED condition up 45% since 2020/21 
                and 13% of unclassified roads in RED condition — both are claim-relevant facts.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                Unclassified roads make up 2,195km — 56% of Warwickshire's 3,929km carriageway network. The council 
                historically surveyed them using Coase Visual Inspection over two years, and only publishes the RED 
                percentage (13% in 2024/25, equivalent to roughly 285km). For 2025/26 it is changing methodology, and 
                the new survey will take three years to complete a full network assessment. Safety inspections are 
                separate from condition surveys — ask when your road was last condition-assessed, not just last driven 
                by an inspector.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does Warwickshire spending above its DfT allocation weaken my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                No. Warwickshire projects £29.936m capital spend against a £25.465m DfT allocation in 2025/26 — roughly 
                18% above the grant — yet still earns an AMBER Spend scorecard. The DfT judges spend against network need, 
                not just the headline figure. The council's own asset management plan acknowledges that available funding 
                is unlikely to match maintenance need, and that reducing preventative treatments will inevitably worsen 
                annual performance.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                The council says its roads compare favourably nationally — does that matter?
              </h3>
              <p className="text-surface-600 text-sm">
                Warwickshire's combined classified-road RCI for 2024/25 shows 4.34% RED against 11% for England and Wales 
                — genuinely better on that benchmark. But the DfT still rates condition AMBER overall, B/C RED roads have 
                risen from 2.9% to 4.2%, and the council itself warns that national comparisons are not representative of 
                success or failure at the local level. Your claim is about the specific defect on your road, not 
                Warwickshire's ranking against Derbyshire.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Pothole repairs spiked to 11,614 in 2023/24 — does the fall to 10,099 mean roads are fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                No. Warwickshire still filled 10,099 potholes in 2024/25 and expects to permanently repair over 12,000 in 
                the current financial year. The five-year total is 39,556 filled potholes from find-and-fix gangs and 
                reactive repairs combined. A network producing defects at that rate is one where potholes routinely form 
                between inspections — exactly where prior reports and photographic evidence decide claims.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Warwickshire is changing its survey methodology — how does that affect my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                From 2026/27 the council will use BSI PAS 2161, which breaks the existing red/amber/green time series. 
                For 2025/26 it is aligning unclassified road surveys with the classified network methodology, but the 
                survey will take three years for a full network pass. If your incident falls during a transition year, 
                ask which survey standard applied to your road and whether condition data existed at all for that location.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.warwickshire.gov.uk/road-works-maintenance/local-highways-maintenance-transparency-report"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Warwickshire County Council Local Highways Maintenance Transparency Report (2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
